import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {
	Asset,
	Company,
	Filter,
	isNilOrWhitespace,
	Mode,
	SortType,
	TagWithCount,
} from 'company-finder-common';
import _ from 'lodash-es';
import { SearchService } from '@Common';
import { DeploymentContext } from '../utilities/deployment-context/deployment-context.js';
import { Summary } from '../utilities/summary/summary.js';

export interface SearchResolverResult {
	companies: Company[];
	totalCompanies: number;
	tagCounts: TagWithCount[];
	summary: Summary;
	assets: Asset[];
	totalAssets: number;
}

@Injectable({ providedIn: 'root' })
export class SearchResultsResolver {
	constructor(
		private searchService: SearchService,
		private deploymentContext: DeploymentContext
	) {}

	public async resolve(
		route: ActivatedRouteSnapshot,
		_state: RouterStateSnapshot
	): Promise<SearchResolverResult> {
		// ADJQ-187: This was a quick approach to deep linking to the set of companies for a location, per Chris Dubois' request
		// ADJQ-777: The location/ & filter/ routes are now deprecated, and their use discouraged, but we keep them around
		//           for backwards compatibility with any existing links in various JnJ material that may be in use.
		const location: string = route.params['location'];
		if (location) {
			this.searchService.filter.locations = location
				.split('+')
				.map((l) => l.trim());
		}
		// Similar to the rationale for ADJQ-187, but could potentially support multiple filters
		const filter: string = route.params['filter'];
		if (filter) {
			try {
				this.searchService.filter = new Filter(JSON.parse(filter));
			} catch (e) {
				// ADJQ-749: If it wasn't JSON, try to directly map it into the filter (e.g. /filter/isBlueKnight)
				if (filter in this.searchService.filter) {
					this.searchService.filter[filter] = true;
				} else {
					// it isn't JSON or a valid filter key, so we would like to know that
					throw e;
				}
			}
		}

		// As part of Universal Asset Navigator implementation, preserving these
		// additional search/filter constraints in the query string was temporary removed/lost,
		// but then restored below to preserve deep linking functionality.
		// Maybe there was a vision for a better approach, but for now, we'll just stick with this...
		const partialFilter = {};
		Object.keys(route.queryParams)
			.filter((qp) => qp !== 'text' && !qp.startsWith('sort')) // These two elements are explicitly handled later
			.map((queryParam) => {
				const queryParamValue = route.queryParams[queryParam];
				if (Array.isArray(this.searchService.filter[queryParam])) {
					const queryParamValueAsArray = decodeURIComponent(
						queryParamValue
					).split(this.searchService.ARRAY_QUERY_PARAM_DELIMITER);
					partialFilter[queryParam] = queryParamValueAsArray;
				} else {
					if (queryParamValue === 'false') {
						partialFilter[queryParam] = false;
					} else if (queryParamValue === 'true' || !queryParamValue) {
						partialFilter[queryParam] = true;
					} else {
						partialFilter[queryParam] =
							decodeURIComponent(queryParamValue);
					}
				}
			});
		// Merge the query string's filter args in with the current filter
		_.merge(this.searchService.filter, partialFilter);
		// Remove unsupported search terms explicitly added but which are not allowed:
		this.removeDisabledOptions(this.searchService.filter);

		if (route.queryParams['text']) {
			const predicate: string = decodeURIComponent(
				route.queryParams['text']
			);
			this.searchService.currentSearchPredicate = predicate;
		}

		if (
			route.queryParams['sortType'] ||
			route.queryParams['sortDirection'] ||
			route.queryParams['sortField']
		) {
			// Establish reasonable defaults if only a subset of parameters are specified
			const sortType =
				route.queryParams['sortType'] ??
				(isNilOrWhitespace(this.searchService.currentSearchPredicate)
					? this.searchService.defaultSortType
					: SortType.Ranking);
			const sortDirection =
				route.queryParams['sortDirection'] ??
				(sortType === SortType.Alpha ? 'asc' : 'desc');

			this.searchService.filter.addSort(
				sortType,
				sortDirection,
				route.queryParams['sortField']
			);
		}

		const fetchSize =
			route.routeConfig.path === `login/debug`
				? 10000
				: this.searchService.initialFetchSize;

		const { items: companies, totalItems: totalCompanies } =
			this.deploymentContext.currentMode === Mode.Company
				? await this.searchService.getCompanies(0, fetchSize)
				: { items: [], totalItems: 0 };

		const { items: assets, totalItems: totalAssets } = this
			.deploymentContext.currentMode
			? await this.searchService.getAssets(0, fetchSize)
			: { items: [], totalItems: 0 };

		const tagCounts = await this.searchService.getTagCounts();
		const summary = this.deploymentContext.comprehensiveSummary;

		return {
			companies,
			totalCompanies,
			tagCounts,
			summary,
			assets,
			totalAssets,
		};
	}

	/** This is may be an unnecessary safety net. There probably isn't any real harm in letting someone impose a filter
	 * for a disabled feature. But, we'll go ahead and prune it.
	 */
	public removeDisabledOptions(searchServiceFilter: Filter): void {
		const featureSwitches = this.deploymentContext.featureSwitches;
		if (!featureSwitches.enableBlueKnight) {
			// Matt said on 5/28/26 that he still wants the Blue Knight indicator for companies
			// So, we need to keep this attribute, but let the feature flag disable all the rest
			// delete searchServiceFilter.isBlueKnight;
		}
		if (!featureSwitches.enableQfc) {
			delete searchServiceFilter.isQFCWinner;
		}
		if (!featureSwitches.enableSectors) {
			delete searchServiceFilter.isCrossSector;
			searchServiceFilter.primarySectors = [];
			searchServiceFilter.primarySubSectors = [];
			searchServiceFilter.secondarySectors = [];
			searchServiceFilter.secondarySubSectors = [];
		}
		if (!featureSwitches.enableJPAL) {
			delete searchServiceFilter.isMyJPALCompaniesOnly;
		}
		if (!featureSwitches.enableFollowCompanies) {
			delete searchServiceFilter.isFollowedCompaniesOnly;
		}
	}
}
